import { Button, Tab, Tabs, useTheme } from '@mui/material';
import { PageHeader } from '../layout/PageHeader';
import { Typography } from '../../components/Typography/Typography';
import { ReactComponent as ExportIcon } from '../../assets/icons/export-blue.svg';
import { ContentWrapper } from '../layout/styled';
import { useAtom, useAtomValue } from 'jotai';
import { isSmallScreenState } from '../../state/UIState';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { OpenTimesheetApprovalsTable } from './components/OpenTimesheetApprovalsTable';
import { useLocation } from 'react-router-dom';
import { TimesheetStatus, User, UserRole } from '../../types';
import { ClosedTimesheetApprovalsTable } from './components/ClosedTimesheetApprovalsTable';
import {
  approvalDetailsModalState,
  tsApprovalsSelectedOPsFilterState,
  tsApprovalsSelectedOYearFilterState,
  tsApprovalsStatusFilter,
} from '../../state/UIAccountantState';
import { ApprovalDetailsModal } from './components/ApprovalDetailsModal';
import { MultiSelectMonthPicker } from '../../components/MultiSelectMonthPicker/MultiSelectMonthPicker';
import { useMonthlyTimesheetsToExport } from '../../queries/hooks/useMonthlyTimesheetsToExport';
import { generateActivityLogsTemplate } from '../../utils/excelMultiExportTimesheet';
import { useFilteredOpsTimesheets } from '../../queries/hooks/useFilteredOpsTimesheets';

enum ApprovalTabs {
  allOpenTS = 'All Open Timesheets',
  closedTS = 'Closed Timesheets',
}

export const TimesheetApprovals = () => {
  const { colors } = useTheme();
  const location = useLocation();
  const params = useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location.search]);
  const today = useMemo(() => {
    return new Date();
  }, []);
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const selectedOpState = useAtomValue(tsApprovalsSelectedOPsFilterState);

  const [selectedTab, setSelectedTab] = useState<ApprovalTabs>(ApprovalTabs.allOpenTS);
  const [approvalDetailsModal, setApprovalDetailsModal] = useAtom(approvalDetailsModalState);
  const [selectedMonths, setSelectedMonths] = useState<number[]>(
    Array.from({ length: today.getMonth() }, (_, i) => i)
  );
  const [selectedYear, setSelectedYear] = useAtom(tsApprovalsSelectedOYearFilterState);
  const [statusFilter, setStatusFilter] = useAtom(tsApprovalsStatusFilter);
  const [shouldExport, setShouldExport] = useState(false);

  const { isLoading: timesheetsLoading, data: operatingPartners } = useFilteredOpsTimesheets(
    `${selectedYear}-01`,
    `${selectedYear}-12`,
    statusFilter
  );

  const handleTabChange = (_: React.SyntheticEvent, newValue: ApprovalTabs) => {
    setSelectedTab(newValue);
  };

  const handleCloseModal = useCallback(() => {
    setApprovalDetailsModal(null);
  }, [setApprovalDetailsModal]);

  const tabStyle = {
    color: colors.text.main,
    textTransform: 'none',
    fontSize: '14px',
    paddingLeft: '1rem',
    paddingRight: '1rem',
  };

  const filteredOPs: User[] = useMemo(() => {
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    return (operatingPartners ?? [])?.map((op) => {
      const filteredTS = op.monthlyTimesheets?.filter((ts) => {
        const isNotCurrentMonthYear = !(ts.month === currentMonth && ts.year === currentYear);
        const matchesSelectedTab =
          selectedTab === ApprovalTabs.allOpenTS
            ? ts.status !== TimesheetStatus.CLOSED
            : ts.status === TimesheetStatus.CLOSED;
        const matchesSelectedMonths = selectedMonths.includes(ts.month - 1);
        return isNotCurrentMonthYear && matchesSelectedTab && matchesSelectedMonths;
      });
      return {
        ...op,
        monthlyTimesheets: filteredTS,
      };
    });
  }, [operatingPartners, selectedTab, selectedMonths, today]);

  const selectedOpsForExport = useMemo(() => {
    if (selectedOpState.length) return selectedOpState;
    return filteredOPs;
  }, [selectedOpState, filteredOPs]);

  const timeRangeParams = useMemo(() => {
    return selectedMonths.map((m) => m + 1).join(`-${selectedYear},`) + `-${selectedYear}`;
  }, [selectedMonths, selectedYear]);

  const { data: timesheets, isLoading: isLoadingTSExport } = useMonthlyTimesheetsToExport({
    userIds: selectedOpsForExport.map((op) => op.id).join(','),
    timeRange: timeRangeParams,
    all: true,
    enabled: shouldExport,
    role: UserRole.ADMIN,
  });

  const handleMonthsChange = useCallback(
    (months: number[]) => {
      setSelectedMonths(months);
    },
    [setSelectedMonths]
  );

  const handleYearChange = useCallback(
    (year: number) => {
      setSelectedYear(year);
      params.set('year', year.toString());
    },
    [setSelectedYear, params]
  );

  const handleExport = useCallback(() => {
    setShouldExport(true);
  }, []);

  useEffect(() => {
    if (shouldExport && !isLoadingTSExport && timesheets) {
      setShouldExport(false);
      generateActivityLogsTemplate(timesheets);
    }
  }, [shouldExport, isLoadingTSExport, timesheets]);

  return (
    <>
      <PageHeader
        title='Timesheet Approvals'
        description='Review, approve, or close submitted timesheets'
        breadcrumbs={[{ title: 'Administration' }, { title: 'Timesheet Approvals' }]}
        actionComponent={
          <>
            <MultiSelectMonthPicker
              selectedMonths={selectedMonths}
              selectedYear={Number(selectedYear)}
              onYearChange={(year) => handleYearChange(year)}
              onMonthsChange={(months) => handleMonthsChange(months)}
            />
            <Button
              variant='outlined'
              startIcon={<ExportIcon />}
              onClick={handleExport}
              style={{
                backgroundColor: colors.surfaceBackground.bg1,
                height: '40px',
                marginRight: '10px',
                width: '180px',
              }}
              disabled={isLoadingTSExport}
            >
              <Typography
                variant='h4'
                color={isLoadingTSExport ? colors.textAccent.disabled : colors.textAccent.default}
                style={{ marginBottom: '-1px' }}
              >
                Export
              </Typography>
            </Button>
          </>
        }
      />
      <ContentWrapper isSmallScreen={isSmallScreen}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          sx={{
            marginBottom: '1rem',
          }}
        >
          <Tab label='Open Timesheets' value={ApprovalTabs.allOpenTS} sx={tabStyle} />
          <Tab label='Closed Timesheets' value={ApprovalTabs.closedTS} sx={tabStyle} />
        </Tabs>
        {selectedTab === ApprovalTabs.allOpenTS && (
          <OpenTimesheetApprovalsTable
            operatingPartners={filteredOPs}
            timesheetsLoading={timesheetsLoading}
          />
        )}
        {selectedTab === ApprovalTabs.closedTS && (
          <ClosedTimesheetApprovalsTable
            operatingPartners={filteredOPs}
            timesheetsLoading={timesheetsLoading}
          />
        )}
      </ContentWrapper>
      {approvalDetailsModal && (
        <ApprovalDetailsModal
          approvalDetailsOpen={!!approvalDetailsModal}
          toggleApprovalDetails={handleCloseModal}
        />
      )}
    </>
  );
};
